(() => {
  'use strict';


  const SELECTOR =
    '[data-rt-social-proof]';


  /*
   * Track initialized instances without
   * polluting the global namespace.
   */

  const instances =
    new WeakMap();



  class ProductSocialProof {

    constructor(root) {

      this.root =
        root;


      this.stockRow =
        root.querySelector(
          '[data-rt-stock-row]'
        );


      this.stockCount =
        root.querySelector(
          '[data-rt-stock-count]'
        );


      this.variantDataElement =
        root.querySelector(
          '[data-rt-variant-data]'
        );


      this.stockThreshold =
        Number(
          root.dataset.stockThreshold || 50
        );


      this.productId =
        String(
          root.dataset.productId || ''
        );


      this.variants =
        this.getVariants();


      this.destroyed =
        false;


      this.onVariantChange =
        this.onVariantChange.bind(this);


      this.init();
    }



    init() {

      if (
        !this.productId ||
        !this.variants.length
      ) {
        return;
      }


      /*
       * Official Be Yours variant event.
       */

      document.addEventListener(
        'variant:change',
        this.onVariantChange
      );


      const currentVariantId =
        this.root.dataset.currentVariant;


      if (currentVariantId) {

        this.updateStock(
          currentVariantId
        );

      }

    }



    getVariants() {

      if (!this.variantDataElement) {
        return [];
      }


      try {

        const parsed =
          JSON.parse(
            this.variantDataElement.textContent
          );


        return Array.isArray(parsed)
          ? parsed
          : [];


      } catch (error) {

        console.warn(
          '[Product Social Proof] Invalid variant JSON.',
          error
        );


        return [];

      }

    }



    onVariantChange(event) {

      if (this.destroyed) {
        return;
      }


      const variant =
        event?.detail?.variant;


      if (
        !variant ||
        !variant.id
      ) {
        return;
      }


      const variantId =
        String(variant.id);


      /*
       * Product pages may contain several product
       * components, recommendations or quick views.
       *
       * Ignore events for variants that don't belong
       * to this particular product.
       */

      const belongsToThisProduct =
        this.variants.some(
          item =>
            String(item.id) ===
            variantId
        );


      if (!belongsToThisProduct) {
        return;
      }


      this.root.dataset.currentVariant =
        variantId;


      this.updateStock(
        variantId
      );

    }



    updateStock(variantId) {

      const variant =
        this.variants.find(
          item =>
            String(item.id) ===
            String(variantId)
        );


      if (!variant) {

        this.hideStock();

        return;

      }


      /*
       * Don't show artificial inventory numbers
       * for variants where Shopify isn't tracking
       * inventory.
       */

      const tracksInventory =
        Boolean(
          variant.inventory_management
        );


      if (!tracksInventory) {

        this.hideStock();

        return;

      }


      const quantity =
        Number(
          variant.inventory_quantity
        );


      if (!Number.isFinite(quantity)) {

        this.hideStock();

        return;

      }


      /*
       * 0 is sold out, therefore don't display
       * "Only 0 left".
       */

      const shouldShow =
        quantity > 0 &&
        quantity <= this.stockThreshold;


      if (!shouldShow) {

        this.hideStock();

        return;

      }


      if (this.stockCount) {

        this.stockCount.textContent =
          String(quantity);

      }


      if (this.stockRow) {

        this.stockRow.hidden =
          false;

      }

    }



    hideStock() {

      if (this.stockRow) {

        this.stockRow.hidden =
          true;

      }

    }



    destroy() {

      if (this.destroyed) {
        return;
      }


      this.destroyed =
        true;


      document.removeEventListener(
        'variant:change',
        this.onVariantChange
      );


      instances.delete(
        this.root
      );

    }

  }



  function initializeSocialProof(
    scope = document
  ) {

    /*
     * scope itself might be the component
     * after a Shopify section reload.
     */

    const roots = [];


    if (
      scope instanceof Element &&
      scope.matches(SELECTOR)
    ) {

      roots.push(scope);

    }


    if (
      typeof scope.querySelectorAll ===
      'function'
    ) {

      roots.push(
        ...scope.querySelectorAll(
          SELECTOR
        )
      );

    }


    roots.forEach(root => {

      if (
        instances.has(root)
      ) {
        return;
      }


      const instance =
        new ProductSocialProof(
          root
        );


      instances.set(
        root,
        instance
      );

    });

  }



  function destroySocialProof(
    scope
  ) {

    if (!scope) {
      return;
    }


    const roots = [];


    if (
      scope instanceof Element &&
      scope.matches(SELECTOR)
    ) {

      roots.push(scope);

    }


    if (
      typeof scope.querySelectorAll ===
      'function'
    ) {

      roots.push(
        ...scope.querySelectorAll(
          SELECTOR
        )
      );

    }


    roots.forEach(root => {

      const instance =
        instances.get(root);


      if (instance) {

        instance.destroy();

      }

    });

  }



  /*
   * Standard page load
   */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeSocialProof();
      },
      {
        once: true
      }
    );


  } else {

    initializeSocialProof();

  }



  /*
   * Shopify Theme Editor section reload.
   */

  document.addEventListener(
    'shopify:section:load',
    event => {

      initializeSocialProof(
        event.target
      );

    }
  );


  document.addEventListener(
    'shopify:section:unload',
    event => {

      destroySocialProof(
        event.target
      );

    }
  );



  /*
   * Be Yours page lifecycle.
   */

  document.addEventListener(
    'page:loaded',
    () => {

      initializeSocialProof();

    }
  );

})();